import { Component, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { combineLatest, ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { BadgeDetailPage } from '../../modals/badge-detail/badge-detail.page';
import { UserService } from '../../../services/user.service';
import { CategoriesService } from '../../../services/categories.service';

interface BadgesViewModel {
  canAssignBadges: boolean;
  categories: {
    name: string;
    badges: {
      id: string;
      icon: string;
      name: string;
      description: string;
      conqueredAt: Date;
      tokenize: boolean;
      tokenUrl: string;
    }[];
  }[];
}

@Component({
  selector: 'app-badges',
  templateUrl: './badges.page.html',
  styleUrls: ['./badges.page.scss'],
})
export class BadgesPage implements OnInit, OnDestroy {
  viewModel: BadgesViewModel;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private categoriesService: CategoriesService,
    private userService: UserService,
    public modalController: ModalController
  ) {}

  ngOnInit() {
    this.loadPageData();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  mockLoadingItems(count: number) {
    return new Array(count);
  }

  async presentBadgeDetailModal({
    id,
    icon,
    name,
    description,
    conqueredAt,
    tokenize,
    tokenUrl,
  }) {
    const modal = await this.modalController.create({
      backdropDismiss: false,
      component: BadgeDetailPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        id,
        icon,
        name,
        description,
        conqueredAt,
        tokenize,
        tokenUrl,
      },
    });

    await modal.present();

    const { data: changed } = await modal.onWillDismiss();
    if (changed) {
      this.loadPageData();
    }
  }

  private loadPageData() {
    this.viewModel = undefined;

    combineLatest([
      this.categoriesService.getCategories(true, { invalidateCache: true }),
      this.userService.getUser({ invalidateCache: true }),
    ])
      .pipe(
        takeUntil(this.destroyed$),
        map(([categories, user]) => ({
          canAssignBadges: user.roles.includes('head'),
          categories: categories.map((category) => ({
            name: category.name,
            badges: category.badges.map(
              ({
                id,
                iconColored,
                iconOutlined,
                name,
                description,
                tokenize,
              }) => {
                const userBadge = user.badges[id];
                return {
                  id,
                  icon: userBadge ? iconColored : iconOutlined,
                  name,
                  description,
                  conqueredAt: userBadge?.conqueredAt,
                  tokenize,
                  tokenUrl: userBadge?.tokenUrl,
                };
              }
            ),
          })),
        }))
      )
      .subscribe((viewModel) => {
        this.viewModel = viewModel;
      });
  }
}
